import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LinkSimple, ArrowsClockwise } from '@phosphor-icons/react';
import {
  IBlockBase,
  ICoachSessionAthlete,
  coachSessionsEndpoints,
} from '../../../endpoints/coachSessions';
import { BlockBase } from './BlockBase';
import { GymLinkDialog } from './GymLinkDialog';
import { PhysicalRefsList } from './PhysicalRefsList';
import styles from './BlockBase.module.scss';
import physStyles from './PhysicalBlock.module.scss';

interface PhysicalBlockProps {
  block: IBlockBase;
  index: number;
  rosterAthletes?: ICoachSessionAthlete[];
  onChange: (patch: Partial<IBlockBase>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

/**
 * Tipo 5 — Fisico / Palestra (v1.1).
 * Blocco che rappresenta lavoro di palestra/atletica dentro la sessione campo.
 * Integrazione Palestra Fase 6 out-of-scope: mostra solo UI placeholder.
 * Spec §4.5.
 */
export const PhysicalBlock: FC<PhysicalBlockProps> = ({
  block,
  index,
  rosterAthletes,
  onChange,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
}) => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const queryClient = useQueryClient();
  const snap = block.gymSchemaSnapshot;
  const hasSchema = !!block.gymSchemaId;
  const refs = block.physicalWorkRefs ?? [];
  const useMulti = refs.length > 0 || !hasSchema;
  const [dialogOpen, setDialogOpen] = useState(false);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['coachSession', sessionId] });

  const unlinkMut = useMutation({
    mutationFn: () =>
      sessionId
        ? coachSessionsEndpoints
            .unlinkGym(sessionId, block.order)
            .then((r) => r.data)
        : Promise.resolve(null),
    onSuccess: () => {
      onChange({ gymSchemaId: undefined, gymSchemaSnapshot: undefined });
      invalidate();
    },
  });

  const syncMut = useMutation({
    mutationFn: () =>
      sessionId
        ? coachSessionsEndpoints
            .syncGym(sessionId, block.order)
            .then((r) => r.data)
        : Promise.resolve(null),
    onSuccess: invalidate,
  });

  return (
    <BlockBase
      index={index}
      blockType="physical"
      title={snap?.title ?? block.exerciseName ?? 'Blocco palestra'}
      durationMinutes={snap?.durationMinutes ?? block.durationMinutes}
      transitionAfterMinutes={block.transitionAfterMinutes}
      onTransitionChange={(v) => onChange({ transitionAfterMinutes: v })}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
    >
      {/* Duration */}
      <div className={styles.fieldGrid} style={{ marginBottom: 12 }}>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Durata (min)</label>
          <input
            className={styles.fieldInput}
            type="number"
            min={1}
            value={block.durationMinutes ?? ''}
            onChange={(e) =>
              onChange({ durationMinutes: Number(e.target.value) || undefined })
            }
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>EWT (min)</label>
          <input
            className={styles.fieldInput}
            type="number"
            min={0}
            value={block.ewt ?? ''}
            onChange={(e) =>
              onChange({ ewt: Number(e.target.value) || undefined })
            }
          />
        </div>
      </div>

      {/* Bug 69f87a4b — Multi-scheda multi-gruppo (default per nuovi blocchi) */}
      {useMulti && sessionId ? (
        <PhysicalRefsList
          sessionId={sessionId}
          blockOrder={block.order}
          refs={refs}
          rosterAthletes={rosterAthletes ?? []}
        />
      ) : hasSchema && snap ? (
        <div className={physStyles.schemaCard}>
          <div className={physStyles.schemaIcon}>
            <LinkSimple size={16} color="#f5b350" />
          </div>
          <div className={physStyles.schemaInfo}>
            <span className={physStyles.schemaTitle}>{snap.title ?? 'Scheda palestra'}</span>
            <span className={physStyles.schemaMeta}>
              {snap.durationMinutes != null && `${snap.durationMinutes}'`}
              {snap.exerciseCount != null && ` · ${snap.exerciseCount} esercizi`}
            </span>
          </div>
          <button
            type="button"
            className={physStyles.schemaChangeBtn}
            onClick={() => syncMut.mutate()}
            disabled={syncMut.isPending}
            title="Risincronizza snapshot"
          >
            <ArrowsClockwise size={12} /> Sync
          </button>
          <button
            type="button"
            className={physStyles.schemaChangeBtn}
            onClick={() => unlinkMut.mutate()}
            disabled={unlinkMut.isPending}
          >
            Scollega
          </button>
        </div>
      ) : null}

      {dialogOpen && sessionId && (
        <GymLinkDialog
          sessionId={sessionId}
          blockOrder={block.order}
          onClose={() => setDialogOpen(false)}
        />
      )}

      {/* Notes */}
      <div className={styles.field} style={{ marginTop: 10 }}>
        <label className={styles.fieldLabel}>Note staff (private)</label>
        <textarea
          className={styles.fieldTextarea}
          value={block.staffNotes ?? ''}
          onChange={(e) => onChange({ staffNotes: e.target.value })}
          placeholder="Note visibili solo allo staff…"
        />
      </div>
    </BlockBase>
  );
};
